// Upgrade page (Phase 5.5). Three plan cards stacked horizontally; clicking
// a paid tier POSTs /v1/billing/checkout and redirects the browser to the
// Stripe-hosted Checkout URL. The current plan (read off the same /v1/usage
// query the BillingPage uses) renders a badge instead of a CTA.
//
// The plan numbers below mirror the runbook's table (Free $0 / Starter
// $49 + 2M tokens / Pro $199 + 25M tokens) and are intentionally hardcoded.
// plan_limits in Postgres is the operational source of truth and the gate
// reads it; this UI surfaces the marketing copy that goes alongside it,
// which doesn't change at runtime. If a tier price moves, the runbook
// + plan_limits + this file all update together.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check, Crown, Sparkles } from 'lucide-react';
import type { CreateCheckoutSessionInput, DowngradePreview, UsageSummary } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { ApiError } from '../../lib/api-client.js';
import { billingApi } from '../../lib/api/billing.js';
import { DowngradeSelectionModal } from '../../components/billing/downgrade-selection-modal.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { formatDate } from '../../lib/format-date.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.js';
import { PageHeader } from '../../components/ui/page-header.js';
import { cn } from '../../lib/utils.js';

type PlanCode = CreateCheckoutSessionInput['planCode'] | 'free';

interface PlanTier {
  code: PlanCode;
  name: string;
  // Display copy — the source of truth for cents/tokens lives in plan_limits.
  priceUsdMonthly: number;
  monthlyTokenCap: string;
  overageRate: string | null;
  features: readonly string[];
  // 'recommended' = Starter (ring + Sparkles + shadow); 'premium' = Pro
  // (primary border + primary CTA, no ring) — keeps Starter as the
  // single recommendation while killing the dead-gray look on Pro.
  accent?: 'recommended' | 'premium';
}

// Caps below mirror migration 0020. Updating one without the other creates a
// gap between what the marketing card promises and what the gate enforces —
// audit revisao-v2.md §6 #11/#16 caught this exact failure mode in the prior
// shape (uniform 20MB upload across tiers, no daily cap visible on Free).
const TIERS: readonly PlanTier[] = [
  {
    code: 'free',
    name: 'Free',
    priceUsdMonthly: 0,
    monthlyTokenCap: '25K tokens',
    overageRate: null,
    features: [
      '1 agent',
      '25K tokens / month',
      '20 messages / day',
      'KB: 1 MB per file · 1 document · 1 MB total',
      'Models: Gemini Flash family + GPT-4o-mini',
      'Hard cap — no overage',
      'Quickstart, guides & API reference',
    ],
  },
  {
    code: 'starter',
    name: 'Starter',
    priceUsdMonthly: 49,
    monthlyTokenCap: '2M tokens',
    overageRate: '$0.01 / 1K tokens',
    features: [
      '5 agents',
      '2M tokens / month',
      'KB: 5 MB per file · 10 documents · 50 MB total',
      'Models: Free + GPT-4.1-mini, Claude Haiku',
      'Overage at $0.01 / 1K tokens',
      'In-app support',
    ],
    accent: 'recommended',
  },
  {
    code: 'pro',
    name: 'Pro',
    priceUsdMonthly: 199,
    monthlyTokenCap: '25M tokens',
    overageRate: '$0.005 / 1K tokens',
    features: [
      'Unlimited agents',
      '25M tokens / month',
      'KB: 10 MB per file · 20 documents · 200 MB total',
      'Models: Starter + Gemini 2.5 Pro, GPT-4o',
      'Overage at $0.005 / 1K tokens',
      'Priority support queue',
      'Custom prompt-injection rules',
    ],
    accent: 'premium',
  },
];

export function BillingUpgradePage() {
  const api = useApiClient();
  // Override global QueryClient defaults — the user can resume via Stripe
  // Portal in another tab and we want the gated state to clear on tab focus.
  const summary = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const currentPlan = summary.data?.plan.code as PlanCode | 'no_subscription' | undefined;
  const cancelAt = summary.data?.plan.cancelAt ?? null;
  const cancelDate = cancelAt ? formatDate(cancelAt) : null;

  return (
    <div className="space-y-6">
      <Link
        to="/billing"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to billing
      </Link>
      <PageHeader
        title="Choose a plan"
        description="Subscriptions are billed by Stripe. Selecting a paid plan opens a Stripe-hosted checkout in this tab; we never see your card."
      />
      {cancelDate !== null && (
        <div
          role="status"
          className="rounded-md border border-warning/30 bg-warning/10 px-4 py-3 text-sm"
        >
          <p className="font-medium">Cancellation scheduled for {cancelDate}.</p>
          <p className="mt-1 text-muted-foreground">
            Resume your subscription on the{' '}
            <Link to="/billing" className="underline-offset-2 hover:underline">
              Billing page
            </Link>{' '}
            to switch plans.
          </p>
        </div>
      )}
      <div className="grid gap-6 pt-3 md:grid-cols-3">
        {TIERS.map((tier) => (
          <PlanTierCard
            key={tier.code}
            tier={tier}
            currentPlan={currentPlan ?? null}
            summary={summary.data ?? null}
          />
        ))}
      </div>
    </div>
  );
}

function PlanTierCard({
  tier,
  currentPlan,
  summary,
}: {
  tier: PlanTier;
  currentPlan: PlanCode | 'no_subscription' | null;
  summary: UsageSummary | null;
}) {
  const isCurrent = currentPlan === tier.code;

  return (
    <Card
      className={cn(
        'relative flex flex-col',
        tier.accent === 'recommended' && 'border-primary shadow-md ring-1 ring-primary/20',
        tier.accent === 'premium' && 'border-2 border-primary bg-primary/5 shadow-lg',
      )}
    >
      {tier.accent === 'premium' && (
        <span
          aria-hidden
          className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm"
        >
          Best Value
        </span>
      )}
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            {tier.accent === 'recommended' && (
              <Sparkles className="h-4 w-4 text-primary" aria-hidden />
            )}
            {tier.accent === 'premium' && (
              <Crown className="h-4 w-4 text-primary" aria-hidden />
            )}
            {tier.name}
          </CardTitle>
          {isCurrent && (
            <Badge variant="success" aria-label="Current plan">
              Current
            </Badge>
          )}
        </div>
        <CardDescription className="pt-2">
          <span className="text-3xl font-semibold tabular-nums text-foreground">
            ${tier.priceUsdMonthly}
          </span>
          <span className="text-sm"> / month</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col justify-between gap-4">
        <ul className="space-y-2 text-sm">
          {tier.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        <CtaForTier
          tier={tier}
          currentPlan={currentPlan}
          hasSummary={summary !== null}
          currentPeriodEnd={summary?.plan.currentPeriodEnd ?? null}
          cancelAt={summary?.plan.cancelAt ?? null}
        />
      </CardContent>
    </Card>
  );
}

function CtaForTier({
  tier,
  currentPlan,
  hasSummary,
  currentPeriodEnd,
  cancelAt,
}: {
  tier: PlanTier;
  currentPlan: PlanCode | 'no_subscription' | null;
  hasSummary: boolean;
  currentPeriodEnd: string | null;
  cancelAt: string | null;
}) {
  // The free plan has no checkout — Stripe doesn't let us mint a checkout
  // session against a $0 product. Downgrades from a paid tier go through the
  // portal (cancellation); this card is informational on the upgrade page.
  if (tier.code === 'free') {
    if (currentPlan === 'free' || currentPlan === 'no_subscription') {
      return (
        <Button variant="secondary" disabled aria-label="Free is your current plan">
          Current plan
        </Button>
      );
    }
    return (
      <p className="text-xs text-muted-foreground">
        To downgrade, cancel the active subscription in the billing portal.
      </p>
    );
  }
  // Paid tiers — render the checkout button. Disabled until /v1/usage settles
  // so the "Current" badge has a chance to render and we don't double-flash.
  // The runtime guard above proved tier.code !== 'free'; the cast narrows
  // the type for CheckoutButton (TS doesn't follow the negation through a
  // destructured prop on its own).
  const paidTier = tier as PlanTier & { code: 'starter' | 'pro' };
  return (
    <CheckoutButton
      tier={paidTier}
      currentPlan={currentPlan}
      disabled={!hasSummary}
      currentPeriodEnd={currentPeriodEnd}
      cancelAt={cancelAt}
    />
  );
}

function CheckoutButton({
  tier,
  currentPlan,
  disabled,
  currentPeriodEnd,
  cancelAt,
}: {
  tier: PlanTier & { code: 'starter' | 'pro' };
  currentPlan: PlanCode | 'no_subscription' | null;
  disabled: boolean;
  currentPeriodEnd: string | null;
  cancelAt: string | null;
}) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Two-step confirm gate on the plan-switch CTA: a plain click flips this
  // to true and the CTA expands into Confirm/Cancel buttons. Only Confirm
  // fires the mutation. Mirrors the pattern in agents/list.tsx and
  // api-keys/list.tsx so destructive-or-billing-impacting actions don't
  // run on a single accidental click. Scoped to the active-sub branch
  // (Stripe-hosted Checkout has its own confirmation page, doesn't need it).
  const [confirmingSwitch, setConfirmingSwitch] = useState(false);
  // Holds the DowngradePreview returned in a selection_required error so
  // the modal can render the customer-pick UI without a separate GET.
  const [downgradePreview, setDowngradePreview] = useState<DowngradePreview | null>(null);
  const isCurrent = currentPlan === tier.code;
  const hasActiveSub = currentPlan === 'starter' || currentPlan === 'pro';

  const checkout = useMutation({
    mutationFn: () =>
      billingApi.createCheckoutSession(api, {
        planCode: tier.code,
        successUrl: `${window.location.origin}/billing?checkout=success`,
        cancelUrl: `${window.location.origin}/billing/upgrade`,
      }),
    onSuccess: ({ url }) => {
      setError(null);
      window.location.assign(url);
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError(
            'Stripe billing is not configured on this environment. Ask an admin to set STRIPE_API_KEY.',
          );
          return;
        }
        if (err.status === 402) {
          setError(
            'This plan is not fully wired in Stripe yet. Ask an admin to finish the Stripe setup.',
          );
          return;
        }
        if (err.status === 409) {
          // Stripe-side pre-flight detected an active sub that our DB
          // hasn't seen yet (race window between Checkout completion and
          // the customer.subscription.created webhook). Force a refetch
          // so the dashboard re-renders with the correct active state —
          // the user will then see "Switch to X" via Change plan instead
          // of "Choose X" via Checkout.
          void queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
          setError(
            'You already have an active subscription on Stripe. Refreshing — use Change plan to switch tiers.',
          );
          return;
        }
      }
      setError(err instanceof Error ? err.message : 'Could not start checkout.');
    },
  });

  const change = useMutation({
    mutationFn: (keepActiveAgentIds?: string[]) =>
      billingApi.changeSubscriptionPlan(api, {
        planCode: tier.code,
        ...(keepActiveAgentIds ? { keepActiveAgentIds } : {}),
      }),
    onSuccess: async () => {
      setError(null);
      setDowngradePreview(null);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await queryClient.invalidateQueries({ queryKey: USAGE_QUERY_KEY });
      window.location.assign('/billing?changed=true');
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          setError(
            'Stripe billing is not configured on this environment. Ask an admin to set STRIPE_API_KEY.',
          );
          return;
        }
        if (err.status === 402) {
          setError(
            'This plan is not fully wired in Stripe yet. Ask an admin to finish the Stripe setup.',
          );
          return;
        }
        if (err.status === 409) {
          setError(
            'Cannot change plan right now — the subscription is in a state that does not support it (already canceled, past due, etc.).',
          );
          return;
        }
        if (err.status === 400 && err.code === 'selection_required') {
          // Backend sent the preview payload — open the modal with it.
          // The customer picks N agents, modal calls change.mutate again
          // with keepActiveAgentIds set.
          const body = err.body as { details?: { preview?: DowngradePreview } } | null;
          const preview = body?.details?.preview;
          if (preview) {
            setDowngradePreview(preview);
            setError(null);
            setConfirmingSwitch(false);
            return;
          }
        }
      }
      setError(err instanceof Error ? err.message : 'Could not change plan.');
    },
  });

  if (isCurrent) {
    return (
      <Button variant="secondary" disabled aria-label={`${tier.name} is your current plan`}>
        Current plan
      </Button>
    );
  }

  if (hasActiveSub) {
    if (cancelAt !== null) {
      return (
        <Button
          disabled
          aria-disabled
          aria-label={`Resume subscription before switching to ${tier.name}`}
          title="Resume your subscription on the Billing page to switch plans"
          variant="secondary"
          className="w-full"
        >
          Resume to switch
        </Button>
      );
    }
    if (confirmingSwitch) {
      const periodEndLabel = currentPeriodEnd
        ? formatDate(currentPeriodEnd)
        : 'the next billing date';
      return (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Proration applied to your next invoice on {periodEndLabel}.
          </p>
          <div className="flex gap-2">
            <Button
              onClick={() => { change.mutate(undefined); }}
              disabled={disabled || change.isPending}
              aria-label={`Confirm switch to ${tier.name}`}
              variant="destructive"
              className="flex-1"
            >
              {change.isPending ? 'Switching…' : 'Confirm'}
            </Button>
            <Button
              onClick={() => { setConfirmingSwitch(false); }}
              disabled={change.isPending}
              aria-label="Cancel plan change"
              variant="ghost"
            >
              Cancel
            </Button>
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-2">
        <Button
          onClick={() => { setConfirmingSwitch(true); }}
          disabled={disabled}
          aria-label={`Switch to ${tier.name}`}
          variant="default"
          className="w-full"
        >
          Switch to {tier.name}
        </Button>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <DowngradeSelectionModal
          open={downgradePreview !== null}
          preview={downgradePreview}
          pending={change.isPending}
          error={null}
          onCancel={() => { setDowngradePreview(null); }}
          onConfirm={(keepActiveAgentIds) => { change.mutate(keepActiveAgentIds); }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Button
        onClick={() => { checkout.mutate(); }}
        disabled={disabled || checkout.isPending}
        aria-label={`Upgrade to ${tier.name}`}
        variant="default"
        className="w-full"
      >
        {checkout.isPending ? 'Redirecting…' : `Choose ${tier.name}`}
      </Button>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
